import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, Key, Copy, Trash, CheckCircle, Clock, Prohibit, ArrowClockwise, ArrowCounterClockwise, HourglassMedium } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS = {
  active: { label: "Active", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10", Icon: Clock },
  redeemed: { label: "Redeemed", cls: "text-secondary border-secondary/40 bg-secondary/10", Icon: CheckCircle },
  revoked: { label: "Revoked", cls: "text-destructive border-destructive/40 bg-destructive/10", Icon: Prohibit },
};

export default function AdminAccess() {
  const { user, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [email, setEmail] = useState("");
  const [expiryDays, setExpiryDays] = useState("0");
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState("");
  const [revokeTarget, setRevokeTarget] = useState(null);

  const isExpired = (l) => {
    if (!l.expires_at) return false;
    return new Date(l.expires_at) < new Date();
  };
  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "");

  const load = async () => {
    const [u, l] = await Promise.all([api.get("/admin/users"), api.get("/admin/licenses")]);
    setUsers(u.data);
    setLicenses(l.data);
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user]);

  if (loading) return <div className="grain min-h-[50vh] flex items-center justify-center font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>;
  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/admin/licenses", { email: email.trim().toLowerCase(), expires_in_days: Number(expiryDays) || null });
      setLastCode(res.data.code);
      setEmail("");
      toast.success("License generated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate");
    } finally {
      setBusy(false);
    }
  };

  const reissue = async (l) => {
    try {
      const res = await api.post(`/admin/licenses/${l.id}/reissue`, {});
      setLastCode(res.data.code);
      toast.success("New code issued");
      await load();
    } catch {
      toast.error("Reissue failed");
    }
  };

  const reactivate = async (l) => {
    try {
      await api.post(`/admin/licenses/${l.id}/reactivate`, {});
      toast.success("License reactivated");
      await load();
    } catch {
      toast.error("Reactivate failed");
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success("Copied");
  };

  const confirmRevoke = async () => {
    try {
      await api.delete(`/admin/licenses/${revokeTarget.id}`);
      toast.success("License revoked");
      setRevokeTarget(null);
      await load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const toggleDbAccess = async (u, enabled) => {
    setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, database_access: enabled } : x)));
    try {
      await api.patch(`/admin/users/${u.user_id}/database-access`, { enabled });
      toast.success(enabled ? "Materials Database access granted" : "Materials Database access revoked");
    } catch {
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, database_access: !enabled } : x)));
      toast.error("Could not update access");
    }
  };

  const activeUsers = users.filter((u) => u.is_admin || u.license_activated).length;

  return (
    <div className="grain mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <UsersThree size={26} weight="bold" className="text-primary" />
        <div>
          <h1 className="font-heading font-black text-white text-2xl">Users &amp; Licenses</h1>
          <p className="text-sm text-muted-foreground">{users.length} registered · {activeUsers} with access · {licenses.length} licenses issued.</p>
        </div>
      </div>

      {/* Generate license */}
      <div className="border border-border bg-card p-5 mb-6">
        <h2 className="font-heading font-bold text-white text-sm mb-3 flex items-center gap-2"><Key size={16} weight="bold" className="text-primary" /> Generate License</h2>
        <form onSubmit={generate} className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="license-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@company.com — the account this license is for"
            className="flex-1 rounded-sm bg-background border-border font-mono text-sm focus-visible:ring-primary"
          />
          <Select value={expiryDays} onValueChange={setExpiryDays}>
            <SelectTrigger data-testid="license-expiry-select" className="w-full sm:w-48 rounded-sm bg-background border-border font-mono text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="0" className="font-mono text-sm">No expiry</SelectItem>
              <SelectItem value="30" className="font-mono text-sm">Expires in 30 days</SelectItem>
              <SelectItem value="90" className="font-mono text-sm">Expires in 90 days</SelectItem>
              <SelectItem value="180" className="font-mono text-sm">Expires in 180 days</SelectItem>
              <SelectItem value="365" className="font-mono text-sm">Expires in 1 year</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="generate-license-btn" type="submit" disabled={busy || !email.trim()} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest px-6">
            {busy ? "..." : "Generate"}
          </Button>
        </form>
        {lastCode && (
          <div data-testid="generated-code" className="mt-3 flex items-center gap-3 border border-primary/40 bg-primary/10 px-3 py-2 rounded-sm">
            <span className="font-mono text-lg text-white tracking-widest">{lastCode}</span>
            <Button onClick={() => copy(lastCode)} size="sm" variant="ghost" className="rounded-sm h-7 text-xs text-primary hover:bg-primary/10"><Copy size={14} className="mr-1" /> Copy</Button>
            <span className="font-mono text-[10px] text-muted-foreground ml-auto">Share this with the user.</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Licenses */}
        <div className="border border-border">
          <div className="px-4 py-2.5 border-b border-border bg-muted font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Licenses</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {licenses.length === 0 && <tr><td className="px-4 py-4 text-muted-foreground text-sm">No licenses yet.</td></tr>}
                {licenses.map((l) => {
                  const expired = isExpired(l) && l.status !== "revoked";
                  const s = expired
                    ? { label: "Expired", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10", Icon: HourglassMedium }
                    : (STATUS[l.status] || STATUS.active);
                  return (
                    <tr key={l.id} data-testid={`license-row-${l.id}`} className="border-b border-border/60 hover:bg-muted/30">
                      <td className="px-4 py-2.5">
                        <div className="font-mono text-white tracking-wider">{l.code}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{l.assigned_email || "any"}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">{l.expires_at ? `expires ${fmtDate(l.expires_at)}` : "no expiry"}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm ${s.cls}`}>
                          <s.Icon size={11} weight="bold" /> {s.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          <Button onClick={() => copy(l.code)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-secondary" title="Copy code"><Copy size={15} /></Button>
                          <Button data-testid={`reissue-btn-${l.id}`} onClick={() => reissue(l)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-primary" title="Reissue new code"><ArrowClockwise size={15} /></Button>
                          {l.status === "revoked" ? (
                            <Button data-testid={`reactivate-btn-${l.id}`} onClick={() => reactivate(l)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-emerald-400" title="Reactivate"><ArrowCounterClockwise size={15} /></Button>
                          ) : (
                            <Button data-testid={`revoke-btn-${l.id}`} onClick={() => setRevokeTarget(l)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive" title="Revoke"><Trash size={15} /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Users */}
        <div className="border border-border">
          <div className="px-4 py-2.5 border-b border-border bg-muted font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Registered Users</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="px-4 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">User</th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Materials DB</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="px-4 py-2.5">
                      <div className="text-white">{u.name || u.email.split("@")[0]}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{u.email}</div>
                      {u.license_code && <div className="font-mono text-[10px] text-muted-foreground">{u.license_code}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      {u.is_admin ? (
                        <span className="border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm">Admin</span>
                      ) : u.license_activated ? (
                        <span className="border border-emerald-400/40 bg-emerald-400/10 text-emerald-400 px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm">Licensed</span>
                      ) : (
                        <span className="border border-border bg-muted text-muted-foreground px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm">No license</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {u.is_admin ? (
                        <div className="flex justify-center">
                          <span data-testid={`db-access-always-${u.user_id}`} className="border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm">Always</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Switch
                            data-testid={`db-access-toggle-${u.user_id}`}
                            checked={!!u.database_access}
                            onCheckedChange={(v) => toggleDbAccess(u, v)}
                          />
                          <span className="font-mono text-[10px] text-muted-foreground w-6">{u.database_access ? "ON" : "OFF"}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent className="rounded-sm bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-white">Revoke this license?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              <span className="font-mono text-white">{revokeTarget?.code}</span> will stop working and any user activated with it will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-revoke-btn" onClick={confirmRevoke} className="rounded-sm bg-destructive hover:bg-destructive/90 text-white">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
